import React, { useState } from "react";
import { Key, Check, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

// Reskinned into the ICE_Man "journey" language. API unchanged (/invite/validate).
// Continue button scrolls to the pricing section (id kept flexible).

var GOLD = "#C9A24E";

export default function VelvetRope() {
  const [code, setCode] = useState("");
  const [valid, setValid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setValid(null);
    const c = code.trim().toUpperCase();
    if (!c) { setError("Enter an invite code."); return; }
    setLoading(true);
    try {
      const r = await api.post("/invite/validate", { code: c });
      setValid(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response && err.response.data && err.response.data.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const continueToPricing = () => {
    var el = document.getElementById("glx-access") || document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="velvet-rope"
      data-testid="velvet-rope"
      style={{
        textAlign: "center",
        padding: "110px 0",
        borderBottom: "1px solid rgba(243,240,233,0.09)",
        background: "color-mix(in srgb, var(--bg,#08070A) 82%, transparent)",
        backdropFilter: "blur(3px)",
        position: "relative",
        zIndex: 2,
      }}
    >
      <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "0 32px" }}>
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            border: "1px solid rgba(243,240,233,0.18)", borderRadius: "999px",
            padding: "7px 14px", fontFamily: "'Space Mono',monospace", fontSize: "10px",
            letterSpacing: ".22em", textTransform: "uppercase", color: GOLD, marginBottom: "8px",
          }}
        >
          <Key style={{ height: "12px", width: "12px" }} /> Velvet rope · cryptographic invite
        </div>

        <h3 style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 400, fontSize: "clamp(32px,5vw,60px)", letterSpacing: "-.03em", lineHeight: 1.0, marginTop: "22px", color: "var(--t1,#F3F0E9)" }}>
          Got a key? <em style={{ fontStyle: "italic", color: GOLD }}>Lift the rope.</em>
        </h3>

        <p style={{ color: "var(--t2,#9A968D)", maxWidth: "52ch", margin: "18px auto 0", fontSize: "16px" }}>
          New buyers: paste your cryptographic invite code to unlock priority onboarding and the tier your key was cut for.
        </p>

        <form onSubmit={submit} data-testid="velvet-rope-form" style={{ display: "flex", gap: "12px", maxWidth: "560px", margin: "38px auto 0", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "240px", display: "flex", alignItems: "center", gap: "12px", border: "1px solid rgba(243,240,233,0.18)", borderRadius: "3px", background: "color-mix(in srgb, var(--ink,#0D0C10) 70%, transparent)", padding: "0 16px", height: "56px" }}>
            <Key style={{ height: "15px", width: "15px", color: GOLD, flexShrink: 0 }} />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="GAURI-369-OMEGA"
              autoComplete="off"
              spellCheck={false}
              data-testid="velvet-rope-input"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--t1,#F3F0E9)", fontFamily: "'Space Mono',monospace", fontSize: "15px", letterSpacing: ".06em", textTransform: "uppercase" }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="velvet-rope-submit"
            style={{ height: "56px", fontFamily: "'Space Mono',monospace", fontSize: "12.5px", padding: "0 26px", borderRadius: "2px", cursor: "pointer", border: "1px solid " + GOLD, background: GOLD, color: "#08070A", display: "inline-flex", alignItems: "center", gap: "9px", opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "Verifying…" : "Verify key"}
            <ArrowRight style={{ height: "16px", width: "16px" }} />
          </button>
        </form>

        {error && (
          <div
            data-testid="velvet-rope-error"
            role="alert"
            style={{ marginTop: "24px", maxWidth: "560px", marginLeft: "auto", marginRight: "auto", padding: "14px 16px", border: "1px solid rgba(243,240,233,0.18)", borderRadius: "3px", fontFamily: "'Space Mono',monospace", fontSize: "13px", color: "var(--t2,#9A968D)" }}
          >
            {error}
          </div>
        )}

        {valid && (
          <div
            data-testid="velvet-rope-success"
            style={{ marginTop: "24px", maxWidth: "560px", marginLeft: "auto", marginRight: "auto", padding: "18px 20px", border: "1px solid " + GOLD, borderRadius: "4px", background: "color-mix(in srgb, " + GOLD + " 8%, transparent)", display: "flex", alignItems: "flex-start", gap: "12px", textAlign: "left" }}
          >
            <div style={{ marginTop: "2px", height: "24px", width: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: GOLD }}>
              <Check style={{ height: "15px", width: "15px", color: "#08070A" }} strokeWidth={3} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 500, fontSize: "16px", color: GOLD }}>{valid.message}</div>
              <p style={{ fontSize: "12.5px", color: "var(--t2,#9A968D)", marginTop: "4px", fontFamily: "'Space Mono',monospace" }}>
                Key: <b style={{ color: "var(--t1,#F3F0E9)" }}>{valid.code}</b>
                {valid.tier_hint && <> · Tier: <b style={{ color: "var(--t1,#F3F0E9)" }}>{String(valid.tier_hint).replace("tier_", "")}</b></>}
                {valid.issued_by && <> · Issuer: <b style={{ color: "var(--t1,#F3F0E9)" }}>{valid.issued_by}</b></>}
              </p>
              <button
                onClick={continueToPricing}
                data-testid="velvet-rope-continue"
                style={{ marginTop: "12px", background: "transparent", border: "none", cursor: "pointer", color: GOLD, fontFamily: "'Space Mono',monospace", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                Continue to your tier <ArrowRight style={{ height: "13px", width: "13px" }} />
              </button>
            </div>
          </div>
        )}

        <p style={{ fontFamily: "'Space Mono',monospace", fontSize: "11px", color: "var(--t3,#5C594F)", marginTop: "28px", letterSpacing: ".04em" }}>
          Keys are single-use. Existing users — use the login dropdown in the top-right.
        </p>
      </div>
    </section>
  );
}
